import React, { FunctionComponent, useMemo } from 'react';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import Translate from 'ui/atoms/translate';
import {
  AdminApi,
  VersionedIssuanceDocument,
  VersionedIssuanceDocumentsApi,
  VersionedIssuanceDocumentsListOrderingEnum,
  VersionedIssuanceDocumentsValuesRetrieveFieldEnum,
} from 'api';
import Date from 'src/ui/atoms/date';
import Link from 'src/ui/atoms/link';
import WithDataRecord from 'src/hoc/WithDataRecord';
import useApiCall from 'src/hooks/use-api-call';

export interface PublicationsHistoryTableProps {
  productId: string;
}

const PublicationsHistoryTable: FunctionComponent<PublicationsHistoryTableProps> = WithDataRecord<
  VersionedIssuanceDocumentsApi,
  VersionedIssuanceDocument
>(VersionedIssuanceDocumentsApi)(
  ({ data, paginationProps, loading, ordering, onOrderBy, productId }) => {
    const { makeAuthenticatedApi } = useApiCall();
    const versionedIssuanceDocumentsApi: VersionedIssuanceDocumentsApi = useMemo(
      () => makeAuthenticatedApi(VersionedIssuanceDocumentsApi),
      [makeAuthenticatedApi],
    );

    return (
      <>
        <DataRecordTable
          onOrderBy={onOrderBy}
          ordering={ordering}
          data={data}
          loading={loading}
          paginationProps={paginationProps}
          cells={[
            {
              title: <Translate name={`publications.table.version`} />,
              render: ({ version }) => version,
              orderingFieldName: VersionedIssuanceDocumentsListOrderingEnum.VERSION_ASC,
              type: CellTypeEnum.text,
              filterName: 'version',
              searchFieldName: VersionedIssuanceDocumentsValuesRetrieveFieldEnum.VERSION,
              getFilterData: (params: any) =>
                versionedIssuanceDocumentsApi.versionedIssuanceDocumentsValuesRetrieve({
                  ...params,
                  productId: productId,
                }),
            },
            {
              title: <Translate name={`publications.historyModal.documentHash`} />,
              render: ({ document }) => document.hashBase64,
              orderingFieldName: VersionedIssuanceDocumentsListOrderingEnum.HASH_BASE64_ASC,
              filterName: 'hashBase64',
              searchFieldName: VersionedIssuanceDocumentsValuesRetrieveFieldEnum.HASH_BASE64,
              getFilterData: (params: any) =>
                versionedIssuanceDocumentsApi.versionedIssuanceDocumentsValuesRetrieve({
                  ...params,
                  productId: productId,
                }),
            },
            {
              title: <Translate name={`publications.table.releaseDate`} />,
              render: ({ publicationDate }) => (publicationDate ? <Date>{publicationDate}</Date> : '-'),
              orderingFieldName: VersionedIssuanceDocumentsListOrderingEnum.PUBLICATION_DATE_ASC,
              type: CellTypeEnum.date,
              filterName: 'publicationDate',
            },
            {
              title: '',
              render: ({ document }) => (
                <Link
                  href={document.file}
                  target="_blank"
                  icon="download"
                  download={true}
                  iconColor="primary"
                  iconSize="large"
                />
              ),
              alignContent: 'right',
            },
          ]}
        />
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.versionedIssuanceDocumentsList({
      offset,
      ordering: ordering as VersionedIssuanceDocumentsListOrderingEnum,
      limit,
      ...props,
      productId: props.productId,
    });
  },
);

export default PublicationsHistoryTable;
